'use client'

import { useState } from 'react'
import Link from 'next/link'
import { X, Settings, Check, Ban } from 'lucide-react'
import { useCookieConsent } from './cookie-consent-context'
import { cn } from '@/lib/utils'

/**
 * GDPR-compliant Cookie Banner for Italy
 *
 * Requirements from Garante (Italian DPA):
 * - Equal prominence buttons (same color, font, size)
 * - Close (X) button that rejects all non-technical cookies
 * - Short notice about technical and profiling cookies
 * - Link to cookie policy
 */
export function CookieBanner() {
  const { showBanner, setShowBanner, acceptAll, rejectAll, consent, updateConsent } = useCookieConsent()
  const [showSettings, setShowSettings] = useState(false)

  if (!showBanner) return null

  // Base button style - equal prominence for all buttons (Italian GDPR requirement)
  const buttonBase = cn(
    'px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200',
    'border border-slate-300 bg-white text-slate-700',
    'hover:bg-slate-50 hover:border-slate-400',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400'
  )

  // Accept All - slightly more visible but not dark pattern
  const acceptButton = cn(
    buttonBase,
    'bg-emerald-600 text-white border-emerald-600',
    'hover:bg-emerald-700 hover:border-emerald-700'
  )

  // Close button style
  const closeButton = cn(
    'p-2 rounded-full transition-all duration-200',
    'text-slate-400 hover:text-slate-600 hover:bg-slate-100',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400'
  )

  const handleReject = () => {
    if (showSettings) {
      // In settings mode, save current selections
      updateConsent(consent.analytics, consent.profiling)
    } else {
      // Close button or Reject All - rejects non-technical cookies
      rejectAll()
    }
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      <div className="max-w-4xl mx-auto bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
        {/* Header with Close Button */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-2xl" role="img" aria-hidden="true">🍪</span>
            <h2 id="cookie-banner-title" className="font-semibold text-slate-900">
              {showSettings ? 'Impostazioni Cookie' : 'Cookie e Privacy'}
            </h2>
          </div>
          <button
            onClick={handleReject}
            className={closeButton}
            aria-label="Chiudi e accetta solo cookie tecnici"
            title="Chiudi e accetta solo cookie tecnici"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {!showSettings ? (
            <>
              {/* Main notice - Italian GDPR compliant text */}
              <p id="cookie-banner-description" className="text-sm text-slate-600 leading-relaxed mb-4">
                Questo sito utilizza <strong>cookie tecnici</strong> necessari al funzionamento
                (autenticazione, sicurezza, sessione) e <strong>cookie di analisi</strong> per
                capire come utilizzi il servizio e migliorarlo. I cookie tecnici sono sempre
                attivi. Per i cookie di analisi, puoi scegliere di accettarli o rifiutarli.
              </p>

              {/* Quick info */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs">
                  <Check size={14} />
                  Cookie tecnici: sempre attivi
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                  <Settings size={14} />
                  Cookie analisi: tua scelta
                </span>
              </div>

              {/* Buttons - Equal prominence as required by Italian guidelines */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={acceptAll}
                  className={acceptButton}
                >
                  Accetta tutti
                </button>
                <button
                  onClick={rejectAll}
                  className={buttonBase}
                >
                  Rifiuta cookie analisi
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className={buttonBase}
                >
                  <span className="flex items-center gap-2">
                    <Settings size={16} />
                    Personalizza
                  </span>
                </button>
                <Link
                  href="/privacy"
                  className="text-sm text-slate-500 hover:text-slate-700 underline ml-auto"
                >
                  Privacy Policy
                </Link>
              </div>
            </>
          ) : (
            <>
              {/* Settings view */}
              <p className="text-sm text-slate-600 mb-4">
                Gestisci le tue preferenze per i cookie. I cookie tecnici sono sempre attivi
                e non possono essere disattivati.
              </p>

              {/* Cookie categories */}
              <div className="space-y-3 mb-4">
                {/* Technical cookies - always on */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">Cookie tecnici</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded">
                        Necessari
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Per il funzionamento del sito: autenticazione, sicurezza, sessione
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600">
                    <Check size={16} />
                    <span className="text-xs font-medium">Attivi</span>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                  <div>
                    <span className="font-medium text-slate-900">Cookie di analisi</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Ci aiutano a capire come utilizzi il sito per migliorarlo (PostHog)
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => updateConsent(e.target.checked, consent.profiling)}
                      className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-xs text-slate-600">
                      {consent.analytics ? 'Attivi' : 'Disattivi'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Settings buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => updateConsent(consent.analytics, consent.profiling)}
                  className={acceptButton}
                >
                  Salva preferenze
                </button>
                <button
                  onClick={rejectAll}
                  className={buttonBase}
                >
                  <span className="flex items-center gap-2">
                    <Ban size={16} />
                    Rifiuta tutti cookie analisi
                  </span>
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-sm text-slate-500 hover:text-slate-700 ml-auto"
                >
                  Torna indietro
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer note */}
        <div className="px-6 py-2 bg-slate-50 border-t border-slate-100">
          <p className="text-xs text-slate-400">
            Le tue preferenze saranno salvate per 12 mesi. Puoi modificarle in qualsiasi momento
            dal link &quot;Cookie&quot; nel footer.
          </p>
        </div>
      </div>
    </div>
  )
}